<!-- This component is used to display the payment step of the Sepio OAP Enrollment Flow -->
<template>
	<SepioFormWithoutProducerNumber
		v-if="useAuthStore().missingSepioGuardApiProducerNumber()"
		:key="`sepio-no-api-${proposedInsured.id}`"
	/>
	<section
		v-else
		class="flex flex-col gap-4 px-6 py-10"
	>
		<div class="grid grid-cols-1 gap-4 *:w-full *:max-w-none">
			<FormKit
				type="dropdown"
				v-model="sepioPayment.paymentType"
				@update:modelValue="setDefaultPaymentDetails"
				name="paymentType"
				:options="[
					{ label: t('document.eft'), value: PlanOapPaymentType.EFT },
					{
						label: t('document.creditCard'),
						value: PlanOapPaymentType.CreditCard,
					},
				]"
				:label="t('document.paymentType')"
				label-class="required mb-2"
				validation="required"
				inner-class="rounded-lg!"
			/>
		</div>

		<hr class="mt-5 mb-9 border-slate-200" />

		<FormKit
			#default="{ state: { valid } }"
			v-model="paymentDetailsModel"
			:key="`oapPaymentDetails-${proposedInsured.id}`"
			type="form"
			:actions="false"
			form-class="w-full grow h-full shrink-0 relative"
		>
			<Transition
				name="fade"
				mode="out-in"
			>
				<OapCreditCardForm
					v-if="sepioPayment.paymentType === PlanOapPaymentType.CreditCard"
				/>
				<EftFields
					v-else
					field-account-holder
					isSepio
					show-check
					v-model="sepioPayment.paymentDetails"
				/>
			</Transition>
			<Teleport
				to="#oapEnrollmentNext"
				v-if="
					sessionStore.getPlanHolder(sessionDocumentsStore.currentPlanHolderID)
						?.oapStep === 'html' &&
					sepioPayment.paymentType === PlanOapPaymentType.EFT
				"
			>
				<button
					type="button"
					class="text-accent-700! h-12 w-52 justify-center rounded-md border-0 font-bold transition-all duration-300"
					:class="
						valid
							? 'bg-accent-400! hover:bg-accent-500! hover:text-accent-800!'
							: 'bg-secondary-300! hover:bg-secondary-300! cursor-not-allowed text-slate-500/50! hover:text-slate-500/50!'
					"
					@click="valid && sessionDocumentsStore.setOapStepTo('document')"
				>
					{{ t('document.authorizePayment') }}
				</button>
			</Teleport>
		</FormKit>
	</section>
</template>

<script setup lang="ts">
import EftFields from '../EftFields.vue'
import type Plan from '@/classes/Plan'
import OapCreditCardForm from '@/components/documents/OapStep/Sepio/CreditCardForm.vue'
import SepioFormWithoutProducerNumber from '@/components/documents/OapStep/Sepio/SepioFormWithoutProducerNumber.vue'
import { OapProvider } from '@/data/oapProviders'
import { PlanOapPaymentType } from '@/enums/PlanOapPaymentType'
import { usePresentationLocale } from '@/i18n'
import { getBlankEft } from '@/mixins/eft'
import { useAuthStore } from '@/stores/auth'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type { SepioPaymentDetails } from '@/types'
import { computed, onBeforeMount } from 'vue'

const { t } = usePresentationLocale()
const sessionStore = useSessionStore()
const sessionDocumentsStore = useSessionDocumentsStore()

const proposedInsured = computed({
	get: () =>
		sessionStore.getPlanHolder(
			sessionDocumentsStore.currentPlanHolderID
		) as Plan,
	set: (value) => proposedInsured.value.update(value),
})

// v-model bridge for payment details
// FormKit emits `any`, so we assert the type to satisfy TS
const paymentDetailsModel = computed({
	get: () => sepioPayment.value.paymentDetails ?? {},
	set: (val) => {
		sepioPayment.value.paymentDetails = val
	},
})

// Get sepio payment details
const sepioPayment = computed<SepioPaymentDetails>(() => {
	return proposedInsured.value.oapPayment.paymentPlan as SepioPaymentDetails
})

onBeforeMount(() => {
	setDefaultPaymentDetails()
})

// If the sepio payment details are empty, fill in sensible defaults
function setDefaultPaymentDetails() {
	if (proposedInsured.value.oapPayment.paymentPlanId == null) {
		throw new Error('No OAP payment plan ID on the OAP payment page')
	}

	if (proposedInsured.value.oapPayment.paymentPlan === null) {
		proposedInsured.value.oapPayment = {
			paymentPlanId: proposedInsured.value.oapPayment.paymentPlanId,
			provider: OapProvider.SEPIO_GUARD,
			paymentPlan: {
				paymentType: PlanOapPaymentType.EFT,
				paymentDetails: getBlankEft(),
			},
		}
	}
}
</script>
